/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : projectConfigReducer.js
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import customConsole from "../../config/customConsole";
import actionType from "../actionTypes";

const initialState = {
  error: false,
  msg: "",
  projectConfig: [],
  projects: [],
  selectedProjectId: null,
  selectedProjectDetails: null,
  variants: [],
  selectedVariantId: null,
  selectedVariantDetails: null,
};

const getArrayPayload = (payload) =>
  Array.isArray(payload?.data) ? payload.data : [];

const getDefaultSelection = (items, idKey) => {
  if (!Array.isArray(items) || items.length === 0) {
    return { selectedId: null, selectedDetails: null };
  }
  return {
    selectedId: items[0]?.[idKey] ?? null,
    selectedDetails: items[0] ?? null,
  };
};

const getValidatedSelection = ({ items, idKey, currentId, currentDetails }) => {
  const matchedItem = items.find(
    (item) =>
      Number.parseInt(item?.[idKey], 10) === Number.parseInt(currentId, 10),
  );
  if (matchedItem) {
    return {
      selectedId: matchedItem[idKey],
      selectedDetails: { ...matchedItem, ...currentDetails, ...matchedItem },
    };
  }
  return getDefaultSelection(items, idKey);
};

const getSelectedByIdOrFirst = ({ items, idKey, targetId }) => {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    items.find(
      (item) => Number.parseInt(item?.[idKey], 10) === Number.parseInt(targetId, 10),
    ) || items[0]
  );
};

const projectConfigReducer = (state = initialState, action) => {
  customConsole(action);
  switch (action.type) {
    case actionType.ADD_PROJECT:
    case actionType.UPDATE_PROJECT:
    case actionType.UPDATE_PROJECT_STATUS:
      return {
        ...state,
        error: action.payload.error,
        msg: action.payload.msg,
      };
    case actionType.GET_PROJECT:
      return {
        ...state,
        error: action.payload.error,
        msg: action.payload.msg,
        projectConfig: action.payload.error ? [] : action.payload.data,
      };
    case actionType.GET_PROJECTS_MAIN: {
      const projects = getArrayPayload(action.payload);
      const { selectedId, selectedDetails } = getValidatedSelection({
        items: projects,
        idKey: "project_id",
        currentId: state.selectedProjectId,
        currentDetails: state.selectedProjectDetails,
      });
      return {
        ...state,
        projects,
        selectedProjectId: selectedId,
        selectedProjectDetails: selectedDetails,
      };
    }
    case actionType.SET_SELECTED_PROJECT_MAIN: {
      const selectedProject = getSelectedByIdOrFirst({
        items: state.projects,
        idKey: "project_id",
        targetId: action.payload,
      });

      console.log("---selected project---", selectedProject);

      return {
        ...state,
        selectedProjectId: selectedProject?.project_id ?? null,
        selectedProjectDetails: selectedProject ?? null,
      };
    }
    case actionType.GET_VARIANTS_MAIN: {
      const variants = getArrayPayload(action.payload);
      const { selectedId, selectedDetails } = getValidatedSelection({
        items: variants,
        idKey: "variant_id",
        currentId: state.selectedVariantId,
        currentDetails: state.selectedVariantDetails,
      });
      return {
        ...state,
        variants,
        selectedVariantId: selectedId,
        selectedVariantDetails: selectedDetails,
      };
    }
    case actionType.SET_SELECTED_VARIANT_MAIN: {
      const selectedVariant = getSelectedByIdOrFirst({
        items: state.variants,
        idKey: "variant_id",
        targetId: action.payload,
      });

      return {
        ...state,
        selectedVariantId: selectedVariant?.variant_id ?? null,
        selectedVariantDetails: selectedVariant ?? null,
      };
    }
    case actionType.RESET_PROJECT_CONFIG:
      return initialState;
    default:
      return state;
  }
};

export default projectConfigReducer;
